/**
 * "Të gjitha llojet" te Balanca e pushimeve — një rresht për punëtor, balanca
 * vjetore plus ditët e çdo lloji tjetër.
 *
 * Asnjë shifër s'llogaritet këtu: çdo vlerë vjen nga `byType` i
 * /api/reports/leave-balance?include=types (backend/services/leaveSummary.js).
 * Ky skedar vetëm i rreshton në kolona dhe i mbledh për rreshtin e totalit.
 */

import * as XLSX from 'xlsx-js-style';

export const ANNUAL_ID = 1;

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * @param {object[]} rows         rreshtat e endpoint-it, me `byType`
 * @param {object[]} leaveTypes   leave_types (Id, Name, Rule)
 * @param {number[]|null} selected  llojet që përdoruesi zgjodhi; null = të gjitha
 * @returns {{ columns: object[], rows: object[], totals: object }}
 *
 * Çdo lloj i zgjedhur merr kolonën e vet, edhe me zero — e zgjodhi përdoruesi.
 * Llojet "në vit" (mjekësori) marrin edhe "mbetur"; ato "për rast" ose pa kufi
 * vetëm ditët e marra, sepse s'kanë çka të mbarojnë.
 */
export function buildLeaveMatrix(rows = [], leaveTypes = [], selected = null) {
  const chosen = new Set((selected ?? leaveTypes.map((lt) => lt.Id)).map(Number));
  const annualType = leaveTypes.find((lt) => Number(lt.Id) === ANNUAL_ID);
  const withAnnual = chosen.has(ANNUAL_ID);
  const others = leaveTypes.filter((lt) => Number(lt.Id) !== ANNUAL_ID && chosen.has(Number(lt.Id)));

  const annualName = annualType?.Name || 'Vjetor';
  const annualSub = annualType?.Rule?.label || '';
  const columns = [
    { key: 'workerName', label: 'Punonjësi', type: 'text' },
    { key: 'department', label: 'Departamenti', type: 'text' },
    ...(withAnnual ? [
      { key: 'annualEntitled', label: `${annualName} · të drejta`, sub: annualSub, type: 'days', group: 'annual' },
      { key: 'annualUsed', label: `${annualName} · përdorur`, type: 'days', group: 'annual' },
      { key: 'annualPending', label: `${annualName} · në pritje`, type: 'days', group: 'annual' },
      { key: 'annualRemaining', label: `${annualName} · mbetur`, type: 'days', group: 'annual', emphasis: true },
    ] : []),
    ...others.flatMap((lt) => {
      const yearly = lt.Rule?.kind === 'yearly';
      const base = { key: `t${lt.Id}`, label: yearly ? `${lt.Name} · përdorur` : lt.Name, sub: lt.Rule?.label || '', type: 'days', typeId: lt.Id };
      return yearly
        ? [base, { key: `t${lt.Id}Remaining`, label: `${lt.Name} · mbetur`, sub: lt.Rule?.label || '', type: 'days', remainingOf: lt.Id }]
        : [base];
    }),
    { key: 'pendingAll', label: 'Në pritje (gjithsej)', type: 'days' },
    { key: 'totalDays', label: 'Gjithsej ditë', type: 'days', emphasis: true },
  ];

  const flat = rows.map((r) => {
    const out = { workerId: r.workerId, workerName: r.workerName, department: r.department };
    let total = 0;
    let pending = 0;
    if (withAnnual) {
      const annual = r.byType?.[ANNUAL_ID] || {};
      out.annualEntitled = r2(annual.entitlementDays ?? r.entitlementDays);
      out.annualUsed = r2(annual.approvedDays ?? r.approvedDays);
      out.annualPending = r2(annual.pendingDays ?? r.pendingDays);
      out.annualRemaining = r2(annual.remainingDays ?? r.remainingDays);
      total += out.annualUsed;
      pending += out.annualPending;
    }
    others.forEach((lt) => {
      const b = r.byType?.[lt.Id] || {};
      out[`t${lt.Id}`] = r2(b.approvedDays);
      out[`t${lt.Id}Pending`] = r2(b.pendingDays);
      if (lt.Rule?.kind === 'yearly') out[`t${lt.Id}Remaining`] = r2(b.remainingDays);
      total += Number(b.approvedDays) || 0;
      pending += Number(b.pendingDays) || 0;
    });
    out.totalDays = r2(total);
    out.pendingAll = r2(pending);
    return out;
  });

  const totals = {};
  columns.filter((c) => c.type === 'days').forEach((c) => {
    totals[c.key] = r2(flat.reduce((a, row) => a + (Number(row[c.key]) || 0), 0));
  });
  totals.workers = flat.length;

  return { columns, rows: flat, totals };
}

/** Libër Excel i thjeshtë për pamjen "Të gjitha llojet" — një fletë, me totalin. */
export function exportLeaveMatrixExcel({ columns, rows, totals, year, company = '', filterLine = '' }) {
  const head = columns.map((c) => (c.sub ? `${c.label} (${c.sub})` : c.label));
  const body = rows.map((r) => columns.map((c) => (c.type === 'days' ? Number(r[c.key]) || 0 : r[c.key] || '')));
  const total = columns.map((c, i) => (i === 0 ? `Totali (${totals.workers})` : c.type === 'days' ? totals[c.key] : ''));
  const aoa = [
    [company || 'Kompania'],
    [`Balanca e pushimeve — të gjitha llojet · ${year}`],
    [filterLine],
    [],
    head, ...body, total,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const bold = { font: { bold: true } };
  head.forEach((_, i) => {
    const h = XLSX.utils.encode_cell({ r: 4, c: i });
    const t = XLSX.utils.encode_cell({ r: 5 + body.length, c: i });
    if (ws[h]) ws[h].s = { ...bold, fill: { patternType: 'solid', fgColor: { rgb: 'E6F4F8' } } };
    if (ws[t]) ws[t].s = bold;
  });
  if (ws.A2) ws.A2.s = { font: { bold: true, sz: 14 } };
  ws['!cols'] = columns.map((c, i) => ({ wch: i === 0 ? 26 : Math.max(12, c.label.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Të gjitha llojet');
  XLSX.writeFile(wb, `balanca-pushimeve-te-gjitha-${year}.xlsx`);
}
